import { severityLabel } from '@/lib/siteHealth'
import type { SiteHealthFinding, SiteHealthLevel } from '@/lib/siteHealth.types'
import { pageLabel, pluralize } from './SiteHealthFormat'
import {
  CARD_CLASS,
  EYEBROW_CLASS,
  effortBadgeClass,
  effortLabel,
  impactLabel,
  severityBadgeClass,
} from './SiteHealthStyles'
import type { QuickWinsProps } from './siteHealthComponents.types'

const IMPACT_BARS: readonly SiteHealthLevel[] = ['low', 'medium', 'high']

function whereLabel(finding: SiteHealthFinding): string {
  if (finding.pageUrl) return pageLabel(finding.pageUrl)

  const count = finding.affectedPageCount ?? 0

  if (count > 1) return pluralize(count, 'page')

  return 'Site-wide'
}

function isLevel(value: string | null | undefined): value is SiteHealthLevel {
  return value === 'high' || value === 'medium' || value === 'low'
}

function ImpactMeter({ level }: { level: SiteHealthLevel | null | undefined }) {
  if (!level) return <span className="text-muted-foreground">—</span>

  const filled = IMPACT_BARS.indexOf(level) + 1

  return (
    <span className="inline-flex items-center gap-2">
      <span className="inline-flex items-end gap-0.5" aria-hidden="true">
        {IMPACT_BARS.map((bar, index) => (
          <span
            key={bar}
            className={`w-1.5 rounded-sm ${index < filled ? 'bg-primary' : 'bg-primary/20'} ${index === 0 ? 'h-2' : index === 1 ? 'h-3' : 'h-4'}`}
          />
        ))}
      </span>
      <span>{impactLabel(level)}</span>
    </span>
  )
}

export default function QuickWins({ findings }: QuickWinsProps) {
  return (
    <section className={`${CARD_CLASS} overflow-hidden`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-border px-6 py-5">
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="text-lg font-semibold">Top quick wins</h2>
          <p className="text-sm text-muted-foreground">
            Sorted by highest impact, lowest effort
          </p>
        </div>
        <span className="rounded-full bg-primary px-2.5 py-0.5 text-xs font-medium tabular-nums text-primary-foreground">
          {findings.length}
        </span>
      </div>

      {findings.length === 0 ? (
        <p className="px-6 py-10 text-center text-sm text-muted-foreground">
          No priority findings were returned for this report.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <caption className="sr-only">Top quick wins</caption>
            <thead className={`bg-muted/50 ${EYEBROW_CLASS}`}>
              <tr>
                <th scope="col" className="w-12 px-6 py-3 font-medium">
                  <span className="sr-only">Rank</span>
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Finding
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Where
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Severity
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Impact
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Effort
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {findings.map((finding, index) => (
                <tr key={finding.id} className="align-middle">
                  <td className="px-6 py-4 tabular-nums text-muted-foreground">
                    {index + 1}
                  </td>
                  <th
                    scope="row"
                    className="px-4 py-4 font-medium text-foreground"
                  >
                    {finding.title}
                  </th>
                  <td className="max-w-[280px] truncate px-4 py-4 text-muted-foreground">
                    {whereLabel(finding)}
                  </td>
                  <td className="px-4 py-4">
                    <span className={severityBadgeClass(finding.severity)}>
                      {severityLabel(finding.severity)}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <ImpactMeter
                      level={
                        isLevel(finding.impactLevel)
                          ? finding.impactLevel
                          : null
                      }
                    />
                  </td>
                  <td className="px-4 py-4">
                    {isLevel(finding.effort) ? (
                      <span className={effortBadgeClass(finding.effort)}>
                        {effortLabel(finding.effort)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
